import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// Both access + refresh tokens are persisted to localStorage. This lets a
// newly-opened tab inherit a still-valid access token instead of forcing a
// refresh on boot — which (because the server rotates + revokes the refresh
// token on every /auth/refresh) was invalidating the token in already-open
// tabs. Client owns token storage per dashboard-api's design (no cookies).
// Trade-off: a token stolen via XSS survives in localStorage rather than
// dying with the tab.

export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  // `role` is added once we land the admin guard on dashboard-api;
  // until then this is undefined and admin routes redirect.
  role?: "user" | "admin";
}

interface AuthState {
  accessToken: string | null;
  accessTokenExp: number | null;        // ms epoch
  refreshToken: string | null;
  refreshTokenExp: number | null;       // ms epoch
  user: AuthUser | null;

  setSession: (s: {
    access_token: string;
    access_token_expires_at: string;
    refresh_token: string;
    refresh_token_expires_at: string;
    user: AuthUser;
  }) => void;
  setUser: (u: AuthUser | null) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      accessTokenExp: null,
      refreshToken: null,
      refreshTokenExp: null,
      user: null,

      setSession: ({ access_token, access_token_expires_at, refresh_token, refresh_token_expires_at, user }) =>
        set({
          accessToken: access_token,
          accessTokenExp: new Date(access_token_expires_at).getTime(),
          refreshToken: refresh_token,
          refreshTokenExp: new Date(refresh_token_expires_at).getTime(),
          user,
        }),
      setUser: (u) => set({ user: u }),
      clear: () =>
        set({
          accessToken: null,
          accessTokenExp: null,
          refreshToken: null,
          refreshTokenExp: null,
          user: null,
        }),
    }),
    {
      name: "tt-auth",
      storage: createJSONStorage(() => localStorage),
      // Persist the full token pair + cached user so a new tab boots
      // already-authenticated and doesn't trigger a refresh (which would
      // rotate the refresh token out from under other open tabs).
      partialize: (state) => ({
        accessToken: state.accessToken,
        accessTokenExp: state.accessTokenExp,
        refreshToken: state.refreshToken,
        refreshTokenExp: state.refreshTokenExp,
        user: state.user,
      }),
    },
  ),
);

export const isAuthenticated = (): boolean => {
  const s = useAuthStore.getState();
  if (!s.accessToken || !s.accessTokenExp) return false;
  return s.accessTokenExp > Date.now();
};

export const isRefreshable = (): boolean => {
  const s = useAuthStore.getState();
  if (!s.refreshToken || !s.refreshTokenExp) return false;
  return s.refreshTokenExp > Date.now();
};
